import { type ReactNode, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Brain, Check, ChevronRight, FileClock, FileCheck2, FilePlus2, Globe, Library, Loader2, Microscope, Search, ShieldCheck, Wrench } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import type { AssistantEvent, AssistantEventType, OpenDocumentRequest } from "@/components/assistant/types";
import { displayDocumentName } from "@/lib/documentName";
import { instantTransition, standardTransition } from "@/lib/motion";
import { cn } from "@/lib/utils";

const EVENT_ICONS: Partial<Record<AssistantEventType, typeof Wrench>> = {
  doc_read: FileClock,
  doc_find: Search,
  doc_created: FilePlus2,
  doc_edited: FileCheck2,
  web_search: Globe,
  corpus_search: Library,
  workflow: Microscope,
  review: ShieldCheck,
};

function StatusDot({ streaming, error }: { streaming?: boolean; error?: boolean }) {
  if (streaming) {
    return <span className="size-2 rounded-full bg-primary animate-pulse" />;
  }
  return <span className={cn("size-2 rounded-full", error ? "bg-destructive" : "bg-success")} />;
}

function ReasoningDisclosure({ text, isStreaming }: { text: string; isStreaming?: boolean }) {
  const [open, setOpen] = useState(Boolean(isStreaming));
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-md bg-background p-2 text-xs">
      <CollapsibleTrigger className="flex w-full cursor-pointer items-center gap-1 font-medium text-muted-foreground">
        <ChevronRight className={cn("size-3.5 transition-transform", open && "rotate-90")} />
        Thought process
      </CollapsibleTrigger>
      <CollapsibleContent>
        <p className="mt-2 whitespace-pre-wrap leading-5">{text}</p>
      </CollapsibleContent>
    </Collapsible>
  );
}

function formatDuration(ms: number): string {
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function eventLabel(event: AssistantEvent) {
  switch (event.type) {
    case "reasoning":
      return "Thought process";
    case "thinking":
      return "Thinking";
    case "tool_call_start":
      return event.name ? `Using ${event.name}` : "Using a tool";
    case "doc_read":
      return `Read ${event.filename}`;
    case "doc_find":
      return event.total_matches === undefined ? `Searching ${event.filename}` : `Found ${event.total_matches} matches in ${event.filename}`;
    case "doc_created":
      return event.isStreaming ? `Drafting ${event.filename}` : `Created ${event.filename}`;
    case "doc_download":
      return `Prepared ${event.filename}`;
    case "doc_replicated":
      return event.error ? `Could not copy ${event.filename}` : `Copied ${event.filename}${event.count > 1 ? ` ${event.count} times` : ""}`;
    case "doc_edited":
      return event.error ? `Could not edit ${event.filename}` : event.isStreaming ? `Editing ${event.filename}` : `Edited ${event.filename}`;
    case "web_search":
      return event.isStreaming ? `Searching the web…` : `Web search: ${event.num_results ?? 0} result${event.num_results === 1 ? "" : "s"}`;
    case "corpus_search":
      return event.isStreaming ? `Searching the corpus${event.query ? `: “${event.query}”` : "…"}` : `Corpus search: ${event.num_results ?? 0} result${event.num_results === 1 ? "" : "s"}`;
    case "workflow": {
      // Drafting workflows carry a ready-made stage label ("Drafting sections (2/5)").
      if (event.label) return event.label;
      if (event.status === "budget_exhausted") {
        return `Corpus review paused — budget reached (${event.started ?? 0} tasks)`;
      }
      if (event.status === "done") {
        const kept = event.kept ?? 0;
        const refuted = event.refuted ?? 0;
        return `Corpus review complete — ${kept} finding${kept === 1 ? "" : "s"}${refuted ? `, ${refuted} refuted` : ""}`;
      }
      const phase = event.phase === "research" ? "researching corpus" : event.phase === "synthesize" ? "synthesizing findings" : event.phase ? `running ${event.phase}` : "reviewing corpus";
      const done = event.completed ?? 0;
      const kept = event.kept ?? 0;
      const refuted = event.refuted ?? 0;
      if (event.phaseCount !== undefined && event.phaseCount > 1) {
        return `Corpus review — ${phase} (${done}/${event.phaseCount} documents · ${kept} ${kept === 1 ? "finding" : "findings"}${refuted > 0 ? ` · ${refuted} refuted` : ""})`;
      }
      return done > 0 ? `Corpus review — ${phase} (${done} done)` : `Corpus review — ${phase}…`;
    }
    case "review": {
      if (event.status === "done") {
        const checked = event.checked ?? 0;
        const corrected = event.corrected ?? 0;
        const unresolved = event.residualFlags?.length ?? 0;
        return `${checked} claim${checked === 1 ? "" : "s"} checked · ${corrected} auto-corrected · ${unresolved} unresolved`;
      }
      if (event.redrafting) return "Checking citations — correcting…";
      const count = event.claimCount ?? 0;
      return count > 0 ? `Checking ${count} claim${count === 1 ? "" : "s"} against sources…` : "Checking citations against sources…";
    }
    case "research_report":
      return "Research report";
    case "content":
      return "";
  }
}

export function TimelineEventRow({ event, compact, onOpenDocument }: { event: AssistantEvent; compact?: boolean; onOpenDocument: (request: OpenDocumentRequest) => void }) {
  if (event.type === "reasoning") {
    if (compact) {
      return (
        <div className="flex items-center gap-2 rounded-md bg-background px-2 py-1.5 text-xs text-muted-foreground">
          <StatusDot streaming={event.isStreaming} />
          <Brain className="size-3.5" />
          <span className="min-w-0 flex-1 truncate">Model thought…</span>
        </div>
      );
    }
    return <ReasoningDisclosure text={event.text} isStreaming={event.isStreaming} />;
  }

  const Icon = EVENT_ICONS[event.type] ?? Wrench;
  const error = "error" in event && Boolean(event.error);

  return (
    <div data-testid={`timeline-${event.type}`} className="flex flex-col gap-1 rounded-md bg-background px-2 py-1.5 text-xs text-muted-foreground">
      <div className="flex items-center gap-2">
        <StatusDot streaming={event.isStreaming} error={error} />
        <Icon className="size-3.5" />
        <span className={cn("min-w-0 flex-1 truncate", event.type === "workflow" && "tabular-nums")}>{eventLabel(event)}</span>
        {event.type === "doc_read" && event.document_id ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => onOpenDocument({ documentId: event.document_id!, filename: event.filename })}
          >
            Open
          </Button>
        ) : null}
      </div>
      {event.type === "workflow" && event.status === "running" && event.inFlight.length ? (
        <div className="min-w-0 text-xs text-muted-foreground" style={{ paddingLeft: 30 }}>
          <span className="flex min-w-0 gap-1">
            <span className="min-w-0 truncate">Reading: {event.inFlight.slice(0, 3).map(displayDocumentName).join(", ")}</span>
            {event.inFlight.length > 3 ? <span className="shrink-0">and {event.inFlight.length - 3} more</span> : null}
          </span>
        </div>
      ) : null}
    </div>
  );
}

interface MessageTimelineProps {
  events: AssistantEvent[];
  isStreaming: boolean;
  hasError: boolean;
  duration_ms?: number;
  onOpenDocument: (request: OpenDocumentRequest) => void;
}

export function MessageTimeline({ events, isStreaming, hasError, duration_ms, onOpenDocument }: MessageTimelineProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [latched, setLatched] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (!isStreaming && !latched && events.length > 0) {
      setCollapsed(true);
      setLatched(true);
    }
  }, [isStreaming, latched, events.length]);

  if (events.length === 0 && !isStreaming) return null;

  const errorCount = events.filter((e) => "error" in e && e.error).length;
  const stepCount = events.length;

  const durationLabel = duration_ms != null ? formatDuration(duration_ms) : undefined;

  let headerLabel: ReactNode;
  if (isStreaming) {
    headerLabel = "Working...";
  } else if (durationLabel) {
    headerLabel = (
      <>
        Worked for <span className="tabular-nums">{durationLabel}</span> · <span className="tabular-nums">{stepCount}</span> step{stepCount === 1 ? "" : "s"}
      </>
    );
  } else {
    headerLabel = (
      <>
        <span className="tabular-nums">{stepCount}</span> step{stepCount === 1 ? "" : "s"}
      </>
    );
  }

  const displayedEvents = isStreaming ? events.slice(-5) : events;
  return (
    <div className="my-3 rounded-md border bg-muted/30">
      <button type="button" data-testid="timeline-toggle" onClick={() => setCollapsed((v) => !v)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-muted-foreground">
        {isStreaming ? (
          <Loader2 className="size-4 animate-spin text-primary" />
        ) : (
          <>
            {!hasError && <Check className="size-4 text-success" />}
            <motion.span className="flex size-4 items-center justify-center" animate={{ rotate: collapsed ? 0 : 90 }} transition={prefersReducedMotion ? instantTransition : standardTransition} aria-hidden>
              <ChevronRight className="size-4" />
            </motion.span>
          </>
        )}
        <span>{headerLabel}</span>
        {!isStreaming && hasError && errorCount > 0 ? (
          <span className="ml-auto rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-semibold tabular-nums text-destructive">
            {errorCount} error{errorCount === 1 ? "" : "s"}
          </span>
        ) : null}
      </button>
      <div className={cn("space-y-2 px-3 pb-3", collapsed && "hidden")}>
        {displayedEvents.map((event) => (
          <TimelineEventRow key={event.id} event={event} compact onOpenDocument={onOpenDocument} />
        ))}
      </div>
    </div>
  );
}
